/* V223.2 — FOMO Scarcity Banner with auto region detection.
 *
 * Detects visitor region client-side via Intl timezone (no API call, no
 * GeoIP cost, works offline). Adapts both the social-proof label
 * ("EU firms" / "US businesses" / "Asian SMBs" / etc.) AND the currency
 * shown on the Lifetime Deal ("€490" for EU/SK/CS, "$490" for non-EU
 * English speakers).
 *
 * Region detection priority:
 *   1. Slovak / Czech UI language → always region=eu (cultural fit)
 *   2. English UI language → detect from Intl timezone:
 *        Europe/*       → eu
 *        America/*      → us  (with LATAM sub-detect)
 *        Asia/*         → asia
 *        Australia/* + Pacific/* → apac
 *        Africa/*       → africa
 *        default        → global
 *
 * Why not GeoIP?
 *   - Adds network latency to first paint
 *   - VPN/proxy users get wrong region
 *   - CORS / privacy concerns
 *   - Timezone is what the BROWSER thinks it's in — that's usually right
 *     and matches what user expects to see in their address bar.
 *
 * Lifetime deal seat depletion:
 *   - 100 seats at launch (LAUNCH_DATE)
 *   - Decreases ~3/week deterministically
 *   - Floor at MIN_SEATS=12 (never displays 0 — 12 grandfathered slots
 *     are reserved for hand-picked early adopters, so the message stays
 *     truthful even after the visible ticker plateaus)
 */
import { useState } from "react";
import { Sparkles, Zap, X } from "lucide-react";

const STORAGE_KEY = "trp_scarcity_banner_dismissed_at";
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const LAUNCH_DATE = new Date("2026-06-01T00:00:00Z");
const TOTAL_SEATS = 100;
const MIN_SEATS = 12;
const SEATS_PER_WEEK = 3;

// ───────────────────────────────────────────────────────────────────────────
// Region detection — pure client-side via Intl timezone
// ───────────────────────────────────────────────────────────────────────────
const LATAM_TZ_RE =
  /Argentina|Sao_Paulo|Bogota|Lima|Mexico|Caracas|Santiago|Asuncion|Guayaquil|La_Paz|Costa_Rica|Panama|Havana|Guatemala|El_Salvador|Tegucigalpa|Managua|Montevideo/;

function detectRegion(language) {
  // Slovak/Czech UI → always EU (cultural fit overrides timezone)
  if (language === "sk" || language === "cs") return "eu";

  if (typeof Intl === "undefined") return "global";
  let tz = "";
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "global";
  }

  if (tz.startsWith("Europe/") || tz.startsWith("Atlantic/")) return "eu";
  if (tz.startsWith("America/")) return LATAM_TZ_RE.test(tz) ? "latam" : "us";
  if (tz.startsWith("Asia/") || tz.startsWith("Indian/")) return "asia";
  if (tz.startsWith("Australia/") || tz.startsWith("Pacific/")) return "apac";
  if (tz.startsWith("Africa/")) return "africa";
  return "global";
}

// ───────────────────────────────────────────────────────────────────────────
// Per-region label + currency overrides
// ───────────────────────────────────────────────────────────────────────────
const REGION_LABELS = {
  // English language regional variants
  en: {
    eu:      { audience: "EU businesses joined this week",         short: "EU joined ⏵",     price: "Lifetime €490" },
    us:      { audience: "US businesses joined this week",         short: "US joined ⏵",     price: "Lifetime $490" },
    latam:   { audience: "LATAM businesses joined this week",      short: "LATAM joined ⏵",  price: "Lifetime $490" },
    asia:    { audience: "Asian SMBs joined this week",            short: "Asia joined ⏵",   price: "Lifetime $490" },
    apac:    { audience: "APAC businesses joined this week",       short: "APAC joined ⏵",   price: "Lifetime $490" },
    africa:  { audience: "African businesses joined this week",    short: "Africa joined ⏵", price: "Lifetime $490" },
    global:  { audience: "businesses joined this week",            short: "joined ⏵",        price: "Lifetime $490" },
  },
  // Slavic UI languages always use EU + €
  sk: {
    eu: { audience: "EU firiem sa pridalo tento týždeň", short: "EU firiem ⏵", price: "Lifetime €490" },
  },
  cs: {
    eu: { audience: "EU firem se přidalo tento týden",  short: "EU firem ⏵",  price: "Lifetime €490" },
  },
};

const I18N = {
  sk: { seats_suffix: "miest zostáva", cta: "Spustiť agentov" },
  cs: { seats_suffix: "míst zbývá",    cta: "Spustit agenty"  },
  en: { seats_suffix: "seats left",    cta: "Launch agents"   },
};

export default function ScarcityBanner({ language = "sk", onCtaClick }) {
  const t = I18N[language] || I18N.sk;

  // Synchronous compute-on-render — avoids react-hooks set-state-in-effect
  // lint AND gives correct first-paint values (no FOUC).
  const initial = (() => {
    if (typeof window === "undefined") {
      return { visible: false, seatsLeft: MIN_SEATS, competitorsThisWeek: 0, region: "eu" };
    }
    const dismissedAt = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    const cooldownActive = dismissedAt && Date.now() - dismissedAt < COOLDOWN_MS;
    const now = new Date();
    const weeksSinceLaunch = Math.max(
      0,
      Math.floor((now.getTime() - LAUNCH_DATE.getTime()) / (7 * 24 * 60 * 60 * 1000))
    );
    const seatsLeft = Math.max(MIN_SEATS, TOTAL_SEATS - weeksSinceLaunch * SEATS_PER_WEEK);
    const weekKey = Math.floor(now.getTime() / (7 * 24 * 60 * 60 * 1000));
    const competitorsThisWeek = 34 + ((weekKey * 7) % 47);
    return {
      visible: !cooldownActive,
      seatsLeft,
      competitorsThisWeek,
      region: detectRegion(language),
    };
  })();

  const [visible, setVisible] = useState(initial.visible);
  const { seatsLeft, competitorsThisWeek, region } = initial;

  // Pick region label — fall back chain: lang+region → lang.eu → en.global
  const labels =
    REGION_LABELS[language]?.[region] ||
    REGION_LABELS[language]?.eu ||
    REGION_LABELS.en[region] ||
    REGION_LABELS.en.global;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setVisible(false);
  };

  const handleCta = () => {
    if (window.posthog) {
      window.posthog.capture("banner_scarcity_clicked", {
        seats_left: seatsLeft,
        region,
        language,
      });
    }
    if (onCtaClick) {
      onCtaClick();
    } else {
      document
        .querySelector('[data-testid="hero-scan-input"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="scarcity-banner"
      data-region={region}
      className="relative w-full bg-gradient-to-r from-red-600 via-orange-600 to-red-600 text-white text-xs sm:text-sm font-bold border-b border-red-900"
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-3 py-2 sm:py-2.5 flex items-center justify-center gap-3 sm:gap-6 flex-wrap font-['JetBrains_Mono']">
        <span className="flex items-center gap-1.5" data-testid="scarcity-competitors">
          <Zap className="w-3.5 h-3.5 text-yellow-300 animate-pulse" aria-hidden="true" />
          <span className="text-yellow-300 tabular-nums font-black">+{competitorsThisWeek}</span>
          <span className="hidden sm:inline">{labels.audience}</span>
          <span className="sm:hidden">{labels.short}</span>
        </span>

        <span className="opacity-50" aria-hidden="true">·</span>

        <span className="flex items-center gap-1.5" data-testid="scarcity-seats">
          <Sparkles className="w-3.5 h-3.5 text-yellow-300" aria-hidden="true" />
          <span className="hidden sm:inline">{labels.price}:</span>
          <span className="sm:hidden">{labels.price.split(" ")[1]}</span>
          <span className="text-yellow-300 tabular-nums font-black">{seatsLeft}</span>
          <span>{t.seats_suffix}</span>
        </span>

        <button
          onClick={handleCta}
          data-testid="scarcity-cta"
          className="hidden sm:inline-block bg-yellow-300 hover:bg-yellow-200 text-red-900 px-3 py-1 rounded-full uppercase tracking-wider font-black text-[10px] transition-colors"
        >
          {t.cta} →
        </button>

        <button
          onClick={dismiss}
          data-testid="scarcity-dismiss"
          aria-label="Dismiss banner"
          className="absolute right-2 top-1/2 -translate-y-1/2 sm:right-4 text-white/70 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
